import { apiClient } from '@/shared/lib/axios';
import { authService } from '@/features/auth/services/authService';
import { isAxiosError } from 'axios';
import type { AxiosError } from 'axios';
import type {
  McmContactListResponse,
  McmContactCreateRequest,
  McmContactCreateResponse,
  McmContactUpdateRequest,
  McmContactUpdateResponse,
  McmContactDeleteResponse,
  McmContactErrorResponse,
} from '../types/mcm-contact.types';

const MCM_CONTACTS_API_PATH = '/api/mcm-contacts';

/**
 * APIエラーレスポンスの`error`フィールドをユーザー向けメッセージとして取り出す
 * （axiosはHTTPエラーステータスをレスポンスボディの内容ごとreject するため、
 * 呼び出し側の`error.message`はaxiosの汎用メッセージになってしまう）
 *
 * axiosエラーであることの判定は呼び出し側で行い、本関数は判定済みの
 * `AxiosError` のみを受け取る（引数の型を実際に扱う対象へ限定する）。
 */
const extractMcmContactApiErrorMessage = ({
  axiosError,
  fallbackMessage,
}: {
  axiosError: AxiosError<McmContactErrorResponse>;
  fallbackMessage: string;
}): string => {
  return axiosError.response?.data?.error || fallbackMessage;
};

/**
 * MCM連絡先のCRUDリクエストをCSRFトークン付きで送信し、
 * 失敗時はAPIのエラーメッセージを持つErrorで投げ直す
 * （元のエラーはcauseとして保持し、ログ調査で状態コード等を追える状態にする。
 * CSRFトークン取得自体の失敗はここでは扱わず、原因を切り分けられるよう素通しする）
 */
const performMcmContactRequest = async <TResponse>({
  sendRequest,
  fallbackMessage,
}: {
  sendRequest: (csrfToken: string) => Promise<TResponse>;
  fallbackMessage: string;
}): Promise<TResponse> => {
  const csrfToken = await authService.getCsrfToken();

  try {
    return await sendRequest(csrfToken);
  } catch (caughtError) {
    const errorMessage = isAxiosError<McmContactErrorResponse>(caughtError)
      ? extractMcmContactApiErrorMessage({ axiosError: caughtError, fallbackMessage })
      : fallbackMessage;
    throw new Error(errorMessage, { cause: caughtError });
  }
};

export const mcmContactApiService = {
  /**
   * MCM連絡先一覧を取得
   */
  getAll: async (): Promise<McmContactListResponse> => {
    const response = await apiClient.get<McmContactListResponse>(
      MCM_CONTACTS_API_PATH
    );
    return response.data;
  },

  /**
   * MCM連絡先を追加
   */
  create: async ({
    request,
  }: {
    request: McmContactCreateRequest;
  }): Promise<McmContactCreateResponse> =>
    performMcmContactRequest({
      fallbackMessage: 'MCM連絡先の追加に失敗しました',
      sendRequest: async (csrfToken) => {
        const response = await apiClient.post<McmContactCreateResponse>(
          MCM_CONTACTS_API_PATH,
          request,
          {
            headers: {
              'x-csrf-token': csrfToken,
            },
          }
        );
        return response.data;
      },
    }),

  /**
   * MCM連絡先を更新
   */
  update: async ({
    id,
    request,
  }: {
    id: number;
    request: McmContactUpdateRequest;
  }): Promise<McmContactUpdateResponse> =>
    performMcmContactRequest({
      fallbackMessage: 'MCM連絡先の更新に失敗しました',
      sendRequest: async (csrfToken) => {
        const response = await apiClient.put<McmContactUpdateResponse>(
          `${MCM_CONTACTS_API_PATH}/${id}`,
          request,
          {
            headers: {
              'x-csrf-token': csrfToken,
            },
          }
        );
        return response.data;
      },
    }),

  /**
   * MCM連絡先を削除
   */
  delete: async ({
    id,
  }: {
    id: number;
  }): Promise<McmContactDeleteResponse> =>
    performMcmContactRequest({
      fallbackMessage: 'MCM連絡先の削除に失敗しました',
      sendRequest: async (csrfToken) => {
        const response = await apiClient.delete<McmContactDeleteResponse>(
          `${MCM_CONTACTS_API_PATH}/${id}`,
          {
            headers: {
              'x-csrf-token': csrfToken,
            },
          }
        );
        return response.data;
      },
    }),
};
